import { useState } from "react";

import {
  LABEEB_TIERS,
  tierRowsOf,
  type LabeebTierRow,
  type LabeebTiers,
} from "../../api/labeeb";
import type { McpCatalog, McpQuotaAll, McpTenantList } from "../../api/mcp";
import {
  allCapsOf,
  capLabel,
  metricPath,
  metricValue,
  tenantsOf,
  type FleetMetricEnvelope,
} from "../../api/mcp";
import {
  CouldNotLook,
  Denied,
  EmptyByDesign,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Notice, Page, Panel, Segmented, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { Bars } from "../../components/charts";
import { ProductPanel, ProductTag } from "../../products/PerProduct";

/**
 * Tiers & credits — one section per product, which is what the design draws.
 *
 * A "tier" is not one thing. It is a row in Labeeb's store and a row in MCP's,
 * and they are set independently:
 *
 *   Labeeb   the envelope a merchant buys — messages a day, messages a month,
 *            tokens, Snowflake queries — and the feature set the tier includes.
 *   MCP      the `*` credit default every tool without its own row falls back
 *            to, from GET /admin/quota.
 *
 * Splitting them by product is not presentation. The design's whole reason for
 * a "Stored as" cell is that the two can disagree, and the note it carries —
 * "MCP has no Enterprise row, Enterprise resolves to Pro" — is exactly such a
 * disagreement, load-bearing and invisible in a merged table.
 */
export function Tiers({ config }: { config: AppConfig }) {
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // STORED vs EFFECTIVE is a real distinction on this API and an easy one to
  // get wrong: a merchant with no entitlements row is stored as nothing and
  // resolved as free. Counting them under free would overstate how many
  // deliberate free-tier decisions exist; counting them nowhere hides that
  // free is what they actually get. The switch shows both rather than picking.
  const [basis, setBasis] = useState<"stored" | "effective">("stored");
  // Real, and the design asks for it: how many merchants sit on each tier.
  // Counted client-side from the list the console already reads — MCP serves
  // no per-tier count of its own.
  const tenants = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  // Real: GET /labeeb/admin/tiers, the envelope panel below. Aliased rather
  // than destructured for the same reason `s` is above — this branches on
  // every failure kind, and `resourceStates.test.ts` regex-matches the
  // literal substring `state.kind === "…"` regardless of which variable
  // precedes it, so the alias keeps the diff readable without changing what
  // the test sees.
  const labeebTiers = useResource<LabeebTiers>(config, LABEEB_TIERS);
  const lt = labeebTiers.state;

  // The fleet figures, and the tier they narrow both blocks to. Clicking a
  // tier is how an operator gets from "8 merchants on Plus" to those eight
  // rows — the count is the way in, not a number to go hunting after.
  const [tierFilter, setTierFilter] = useState<string | null>(null);
  const tenantsCount = useResource<FleetMetricEnvelope<"tenants_count">>(
    config,
    metricPath("tenants_count"),
  );
  const usageToday = useResource<FleetMetricEnvelope<"usage_today">>(
    config,
    metricPath("usage_today"),
  );
  const overrides = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );

  return (
    <Page
      title="Tiers & limits"
      subtitle="What a tier entitles a merchant to, in each product that stores one."
    >
      <div className="flex flex-col gap-6">
        <TierMetrics
          tenantsCount={tenantsCount}
          usageToday={usageToday}
          overrides={overrides}
          tierFilter={tierFilter}
          onFilter={setTierFilter}
        />

        {/* ONE BLOCK PER PRODUCT. A tier is two rows in two stores — Labeeb's
            message/token envelope and MCP's per-tool credit caps — and they
            are set independently. They used to be three peer panels plus a
            separate "Tool caps" page reading the SAME /mcp/admin/quota this
            one does, with a Notice telling the reader to go there to change
            anything. */}
        <Card
          title="Labeeb"
          aside={<ProductTag id="labeeb" />}
          note="The envelope a tier grants: messages, tokens, Snowflake queries"
          collapsible
        >
        <ProductPanel
          id="labeeb"
          title="Envelope shape"
          what="What each tier grants — messages a day and a month, tokens, Snowflake queries"
          note="What each tier grants, and whether it is a stored row or a code fallback"
          minHeight={260}
        >
          {lt.kind === "loading" && <Loading what="tier envelopes" />}
          {lt.kind === "signedout" && <SignedOut />}
          {lt.kind === "denied" && <Denied message={lt.error.message} />}
          {lt.kind === "unavailable" && (
            <CouldNotLook
              what="tier envelopes"
              message={lt.error.message}
              onRetry={labeebTiers.reload}
            />
          )}
          {lt.kind === "notfound" && (
            <LoadFailed what="tier envelopes" message="Route not found." />
          )}
          {(lt.kind === "invalid" || lt.kind === "error") && (
            <LoadFailed what="tier envelopes" message={lt.error.message} />
          )}

          {lt.kind === "ready" && (() => {
            // Narrowed by the tier filter above. Labeeb's ids are the same
            // strings MCP's distribution counts, so one filter drives both.
            const rows = tierRowsOf(lt.data).filter(
              (r) => !tierFilter || r.tier_id === tierFilter,
            );
            // FIXED COLUMNS now. This used to union the keys present across
            // every row, because the envelope was an open map and a renamed
            // limit should shrink the table rather than leave a blank column.
            // `TierOut` has exactly two named integers, so there is nothing
            // left to discover.
            const cols = [...LABEEB_LIMIT_KEYS];

            return rows.length === 0 ? (
              <Notice title="No tiers returned">
                The response carried an empty or missing tier list.
              </Notice>
            ) : (
              <Panel className="p-4">
                <Table
                  head={
                    <tr>
                      <Th>Tier</Th>
                      {cols.map((c) => (
                        <Th key={c} right>
                          {limitLabel(c)}
                        </Th>
                      ))}
                      <Th right>Open</Th>
                    </tr>
                  }
                >
                  {rows.map((row) => (
                    <tr key={row.tier_id}>
                      <Td>
                        {/* The id, not a display name: `TierOut` no longer
                            carries one, and fetching the catalog solely to
                            prettify a label would add a request that can fail
                            and leave the column blank. The id is what a write
                            submits, so it is also the safer thing to show. */}
                        <span className="ref">{row.tier_id}</span>
                        {/* `source: "code_default"` is gone — a tier now
                            either exists or the route 404s, so there is no
                            "saving creates it" case left to warn about.
                            `is_default` is a different fact and worth its own
                            badge: it is the tier a business lands on. */}
                        {row.is_default && <Badge>default</Badge>}
                      </Td>
                      {cols.map((c) => (
                        <Td key={c} right>
                          <span className="tabular-nums">{limitValue(row, c)}</span>
                        </Td>
                      ))}
                      <Td right>
                        {/* A link, not an expander. The editor used to open
                            inside this cell, which is why its fields, its two
                            warnings and its result all had to fit in a column
                            — MCP's equivalent got a page. This is that page. */}
                        <Button outline href={labeebTierHref(row.tier_id)}>
                          Open
                        </Button>
                      </Td>
                    </tr>
                  ))}
                </Table>

                {/* ONE TIER IS THE NORMAL STATE, not a truncated read.
                    Labeeb 2.0 stores tiers in its own single table
                    (pk=QUOTA_TIERS, sk=TIER#<id>) and the deploy bootstraps
                    exactly one, `standard`. The v1 stack's QuotaTiers table
                    has more rows and Labeeb 2.0 never reads it.
                    Said here because "only standard" looks like a bug — it
                    cost a round of investigation to establish that it is not,
                    and the next person should not repeat that. */}
                {rows.length === 1 && (
                  <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                    One tier is expected on a fresh environment: the deploy bootstraps{" "}
                    <span className="ref">standard</span> and nothing else. More tiers appear
                    once somebody creates them — there is no seed script, by design, so every
                    tier gets a version and an audit row.
                  </p>
                )}
              </Panel>
            );
          })()}
        </ProductPanel>

        {/* The right-hand half of the design's Envelope shape, for the product
            that CAN answer it. MCP knows which tier each merchant is on, so
            this is real — and the switch is the one the design's metric
            selector idiom is worth spending on. */}
        </Card>

        <Card
          title="MCP"
          aside={<ProductTag id="mcp" />}
          note="Per-tool daily credit caps, and how tenants are spread across tiers"
          collapsible
        >
        <div className="flex flex-col gap-6">
        <Card
          title="Tenants on each tier"
          aside={<ProductTag id="mcp" />}
          note="How the fleet is distributed, by what is stored and by what the server resolves"
        >
          {tenants.state.kind === "ready" ? (
            (() => {
              const all = tenantsOf(tenants.state.data);
              const stored = new Map<string, number>();
              for (const m of all) {
                const key = basis === "stored" ? (m.tier ?? "not set") : (m.tier ?? "free");
                stored.set(key, (stored.get(key) ?? 0) + 1);
              }
              const rows = [...stored.entries()]
                .sort(([a], [b]) => (a === "not set" ? 1 : b === "not set" ? -1 : a.localeCompare(b)))
                .map(([tier, n]) => ({
                  label: tier,
                  value: n,
                  note: `of ${all.length}`,
                  // "not set" is not a tier — it is merchants nobody has
                  // touched, and it deserves its own tone rather than sitting
                  // in the same colour as a decision someone made.
                  tone: tier === "not set" ? ("mute" as const) : ("brand" as const),
                }));

              return (
                <>
                  <div className="mb-4 flex justify-end">
                    <Segmented
                      label="Count tenants by"
                      value={basis}
                      onChange={setBasis}
                      options={[
                        { value: "stored", label: "Stored" },
                        { value: "effective", label: "Effective" },
                      ]}
                    />
                  </div>
                  <Bars rows={rows} max={all.length} />

                  {/* The same counts, as filters. The chart SHOWS the spread;
                      these act on it — clicking one narrows both product
                      blocks to that tier, and clicking it again clears. */}
                  <div className="mt-4">
                    <StatRow>
                      {rows.map((r) => (
                        <Stat
                          key={r.label}
                          value={r.value}
                          label={r.label}
                          onClick={() =>
                            setTierFilter(tierFilter === r.label ? null : r.label)
                          }
                          active={tierFilter === r.label}
                        />
                      ))}
                    </StatRow>
                  </div>
                  <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                    {basis === "stored" ? (
                      <>
                        What is written down. <span className="ref">not set</span> is a merchant
                        with no entitlements row — nothing has been decided for them, and a read
                        never creates one.
                      </>
                    ) : (
                      <>
                        What the server applies. Every merchant with no row resolves as{" "}
                        <span className="ref">free</span>, so free grows by exactly the number
                        that were <span className="ref">not set</span>.
                      </>
                    )}
                  </p>
                </>
              );
            })()
          ) : (
            <Loading what="the tier distribution" />
          )}
        </Card>

        {/* The per-tier cap rows, absorbed from the old /policy/caps index.
            That page read the SAME /mcp/admin/quota this one does and did not
            write — it was a drill-down list, and a drill-down list one click
            from its own summary belongs on the summary. The EDITOR stays on
            its own page: /policy/caps/{tier} is where a cap is actually
            written, and that is a different kind of surface. */}
        <CapRows config={config} quota={quota} tierFilter={tierFilter} />
        </div>
        </Card>
      </div>
    </Page>
  );
}

/**
 * A limit key rendered for a human — `DailyMessageLimit` becomes "Daily
 * message limit" — rather than hard-coding a label per key the reference doc
 * happens to name today. A renamed or added limit still reads as words
 * instead of falling back to the raw key or, worse, being dropped silently.
 */
export function limitLabel(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * One tier's value for one limit column.
 *
 * `-1` means unlimited per the reference doc — render the word, never the
 * literal digits. A `code_default` tier's `limits` is `null`, and a tier
 * simply missing one key (rather than the whole map) is equally possible —
 * both render `—`, never `0`, since `0` is itself a real, meaningfully
 * different value (a tier granted nothing).
 */
/**
 * The console URL for one Labeeb tier.
 *
 * Beside `limitValue` rather than in `api/labeeb.ts`: that file holds the
 * product's API paths, and this is a route in this app — `labeebTierPath` is
 * the PATCH endpoint and the two must not be mistaken for each other.
 */
export function labeebTierHref(tierId: string): string {
  return `/policy/tiers/${encodeURIComponent(tierId)}`;
}

/**
 * One of a tier's two limits, by key.
 *
 * The envelope used to be an open `limits: Record<string, number>` whose keys
 * came from the response, so a renamed limit shrank the table instead of
 * leaving dashes. The new shape has exactly two named integers, so the keys
 * are fixed and this reads them by name.
 *
 * `-1` is unlimited and a missing number is a dash — never a zero, which
 * would read as "no messages allowed".
 */
export function limitValue(row: LabeebTierRow, key: string): string {
  const v = key === "daily" ? row.daily : key === "monthly" ? row.monthly : undefined;
  if (v === undefined || v === null) return "—";
  if (v === -1) return "Unlimited";
  return String(v);
}

/** The two limits every Labeeb tier carries, in render order. */
export const LABEEB_LIMIT_KEYS = ["daily", "monthly"] as const;

/**
 * Which attributes a tier write may name.
 *
 * `editable_attributes` from the response when it is there, falling back to
 * the columns actually present. `Features` is deliberately NOT editable —
 * nothing in the runtime reads it, so offering it would imply a gate that
 * does not exist — and the server's own list is the authority on that.
 */
export function editableAttributesOf(
  _data: LabeebTiers | undefined | null,
  _fallback: string[],
): string[] {
  // `editable_attributes` is gone from the response: `PATCH /admin/tiers/{id}`
  // takes `daily` and `monthly` and nothing else, so the editable set is fixed
  // by the API rather than advertised per response. Keeping the function
  // signature means its two call sites did not have to change shape.
  return [...LABEEB_LIMIT_KEYS];
}

/**
 * Every tier's cap rows, one collapsible card apiece.
 *
 * Absorbed from the old `/policy/caps` index. That page read the same
 * `/mcp/admin/quota` as the summary above it, wrote nothing, and its own
 * Notice told the reader to come here to change anything — so it was a
 * drill-down list one click away from its own summary.
 *
 * The editor is NOT absorbed. `/policy/caps/{tier}` stays its own page: it
 * writes, it carries the resolver warning at the moment a `search_*` tool is
 * capped, and it holds the quota audit. A list and an editor are different
 * kinds of surface.
 */
function CapRows({
  config,
  quota,
  tierFilter,
}: {
  config: AppConfig;
  quota: ReturnType<typeof useResource<McpQuotaAll>>;
  /** The tier the metric row narrowed to, or null for every tier. */
  tierFilter: string | null;
}) {
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");
  const s = quota.state;

  if (s.kind !== "ready") return null;

  const caps = allCapsOf(s.data);
  const tiers = Object.keys(caps)
    .sort()
    .filter((t) => !tierFilter || t === tierFilter);

  return (
    <>
      <Notice title="Reading a cap">
        <span className="ref">-1</span> unlimited · <span className="ref">0</span> gated, never
        allowed · <span className="ref">N</span> metered, N credits a day resetting at 00:00
        UTC. A tool with no row inherits the tier's <span className="ref">*</span> default.
      </Notice>

      {/* Catalog note: the old page also rendered a "Tools & resolvers"
          reference list here. It answered "what exists", not "what does this
          tier cost", and made the page long without belonging to it. The
          catalog is still fetched by the editor, where capping happens. */}
      {tiers.map((tier) => {
        const tierCaps = caps[tier] ?? {};
        const entries = Object.entries(tierCaps).sort(([a], [b]) =>
          a === "*" ? -1 : b === "*" ? 1 : a.localeCompare(b),
        );

        return (
          <Card
            key={tier}
            title={<span className="ref">{tier}</span>}
            note={
              <>
                {entries.length} cap row{entries.length === 1 ? "" : "s"}
              </>
            }
            aside={
              <Button outline href={`/policy/caps/${encodeURIComponent(tier)}`}>
                Open
              </Button>
            }
            collapsible
          >
            {entries.length === 0 ? (
              <EmptyByDesign scope={`tier ${tier}`} />
            ) : (
              <Table
                className="[--gutter:0px]"
                head={
                  <tr>
                    <Th>Tool</Th>
                    <Th right>Cap</Th>
                  </tr>
                }
              >
                {entries.map(([tool, n]) => {
                  const c = capLabel(n);
                  return (
                    <tr key={tool}>
                      <Td>
                        <span className="ref">{tool}</span>
                        {tool === "*" && (
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                            default for every tool without a row
                          </span>
                        )}
                      </Td>
                      <Td right>
                        <Badge tone={c.tone}>{c.text}</Badge>
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>
        );
      })}
      {catalog.state.kind === "denied" && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The tool catalog is not readable with your access, so a cap row cannot be
          checked against the tools that exist.
        </p>
      )}
    </>
  );
}

/**
 * The fleet figures for this page, and the tier filter they drive.
 *
 * Each stat is the way INTO what it counts: clicking a tier narrows both
 * product blocks to it, so "8 merchants on Plus" leads to those eight rather
 * than leaving an operator to find them. Clicking the active one clears.
 *
 * `tier_distribution` is per-tier, so it renders one stat per tier — which is
 * the same data the "Tenants on each tier" chart below shows, made actionable
 * instead of decorative.
 */
function TierMetrics({
  tenantsCount,
  usageToday,
  overrides,
  tierFilter,
  onFilter,
}: {
  tenantsCount: ReturnType<typeof useResource<FleetMetricEnvelope<"tenants_count">>>;
  usageToday: ReturnType<typeof useResource<FleetMetricEnvelope<"usage_today">>>;
  overrides: ReturnType<typeof useResource<FleetMetricEnvelope<"quota_overrides_count">>>;
  tierFilter: string | null;
  onFilter: (tier: string | null) => void;
}) {
  // "…" while a metric loads, never 0 — a zero an operator reads as a fact is
  // worse than an obvious gap, and these are the numbers decisions hang on.
  // metricValue returns null until its state is ready, which is what "…"
  // renders from — never 0. A zero an operator reads as a fact is worse than
  // an obvious gap, and these are numbers decisions hang on.
  const count = metricValue(tenantsCount.state);
  const usage = metricValue(usageToday.state);
  const overrideCounts = metricValue(overrides.state);
  const overrideTotal = overrideCounts
    ? Object.values(overrideCounts).reduce<number>(
        (n, v) => n + (typeof v === "number" ? v : 0),
        0,
      )
    : null;

  return (
    <StatRow>
      <Stat value={count ?? "…"} label="Merchants" />
      <Stat value={usage?.total_credits ?? "…"} label="Credits today" />
      <Stat
        value={overrideTotal ?? "…"}
        label="Cap overrides"
        hint="tools with their own row, across every tier"
      />
      {tierFilter && (
        // The escape hatch. The per-tier stats live in the chart below, so
        // without this a filter set there could only be cleared by going back
        // to find the same control.
        <Stat
          value="Clear"
          label={`Filtered to ${tierFilter}`}
          onClick={() => onFilter(null)}
          active
        />
      )}
    </StatRow>
  );
}
